// צורות התשובה של /api/conversations*, /api/panel/*, /api/consents*, /api/tree/proposals/:id/decide,
// /api/connector/pairings/:code/secrets, /api/artifacts/:slug/interfaces כפי שמסכי "מלא" צורכים אותן (10, 08, 05, 02, 09).
// החוזים קובעים endpoints ולא תמיד גופים — מה שמסומן ⚠ הוא ההצעה של 11; הבעלים מאמץ או מתקן, ואז מתקנים כאן.
// ה-mock (apps/web/mock/ai.ts) מגיש בדיוק את הצורות האלה.
import { z } from 'zod';
import { ConsentItemStatus, ConsentStatus, ConversationAttachment, ProposalKind, Role, SseEvent, TurnStatus } from '@aibify/core';

const IsoTs = z.string();

/* ─── שיחות (10 §1) ─── */

/** ⚠ GET /api/conversations → {conversations} */
export const ConversationSummary = z.object({
  id: z.string(),
  level: z.union([z.literal(1), z.literal(2)]),
  titleHe: z.string().nullable(),
  status: z.enum(['active', 'idle', 'archived']),
  lastActiveAt: IsoTs,
  /** web-conversation-list-search: רק בתוצאת חיפוש שההתאמה בה בהודעה ולא בכותרת */
  snippetHe: z.string().optional(),
});
export type ConversationSummary = z.infer<typeof ConversationSummary>;
export const ConversationList = z.object({ conversations: z.array(ConversationSummary) });

export const CreateConversationResponse = z.object({ id: z.string() });
/** קובץ מצורף (10 §1): מה שחוזר מ-POST /files ומופיע ב-history / ב-user_message. */
export const Attachment = ConversationAttachment;
export type Attachment = z.infer<typeof Attachment>;
export const UploadFileResponse = z.object({ file: Attachment.extend({ textName: z.string().optional() }) });
/** מגבלות ההעלאה — כמו ב-api (conversations/files.ts); המסך בודק מוקדם כדי לא להעלות לשווא, השרת אוכף. */
export const UPLOAD_LIMITS = { fileBytes: 25 * 1024 * 1024, perMessage: 10 };
/** conversation-voice-input: GET /api/voice — כפתור ההקלטה מוצג רק כש-enabled. */
export const VoiceInfo = z.object({ enabled: z.boolean(), maxSeconds: z.number().int().positive(), maxBytes: z.number().int().positive() });
export const VoiceTranscript = z.object({ text: z.string() });
export const UPLOAD_ACCEPT = '.png,.jpg,.jpeg,.gif,.webp,.pdf,.csv,.txt,.md,.json,.xlsx,.xls,.docx';
/** POST /turns בזמן תור חי (10 §1 הודעה באמצע תור) → אותו turnId + sayId של ההודעה שנכנסה אליו; אחרת תור חדש בלי sayId. */
export const CreateTurnResponse = z.object({ turnId: z.string(), sayId: z.string().optional() });

/** אירוע SSE עם seq (id של ה-SSE) ו-turnId (⚠ תוספת ב-data: לאיזה תור האירוע שייך). */
export type SeqEvent = SseEvent & { seq: number; turnId?: string };

/**
 * אירוע מהשרת → SseEvent. בלוק לא מאומת כאן (display מאמת בלוק-בלוק; לא-מוכר → raw) — אחרת בלוק
 * מסוג חדש היה מפיל את כל האירוע. אירוע מסוג לא מוכר → null (מדלגים, לא זורקים).
 */
export function parseSseEvent(raw: unknown): (SseEvent & { turnId?: string; secret?: boolean }) | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const extra = { ...(typeof o.turnId === 'string' ? { turnId: o.turnId } : {}), ...(o.secret === true ? { secret: true } : {}) };
  if (o.type === 'block' && 'block' in o) {
    return { type: 'block', block: o.block as never, ...(typeof o.asOf === 'string' ? { asOf: o.asOf } : {}), ...(o.focus === 'expand' || o.focus === 'inline' ? { focus: o.focus } : {}), ...extra };
  }
  const p = SseEvent.safeParse(o);
  return p.success ? { ...p.data, ...extra } : null;
}

/** ⚠ GET /api/conversations/:id/history — תורים שהסתיימו + אירועיהם (משוחזרים מ-sys.turn_event, אחרי scrub). */
export const HistoryTurn = z.object({
  id: z.string(),
  seq: z.number().int(),
  userText: z.string(),
  status: TurnStatus,
  /** קבצים שצורפו להודעת הפתיחה של התור (הודעות באמצע תור נושאות את שלהן ב-user_message). */
  attachments: z.array(Attachment).default([]),
  /** sys.turn started_at/finished_at (ISO) — "עבד …" / "לפני …" בקיפול של התור (11). */
  startedAt: z.string().nullable().optional(),
  finishedAt: z.string().nullable().optional(),
  events: z.array(z.object({ seq: z.number().int() }).passthrough()),
});
export type HistoryTurn = z.infer<typeof HistoryTurn>;
export const HistoryResponse = z.object({
  conversation: ConversationSummary.extend({
    /** השרת מחליט אם אפשר להמשיך (v1 sessions.mjs) — המסך לא מנחש. */
    canContinue: z.boolean(),
    canContinueReasonHe: z.string().nullable().optional(),
  }),
  turns: z.array(HistoryTurn),
  /** seq של האירוע האחרון בהיסטוריה — Last-Event-ID לחיבור ה-SSE. */
  lastSeq: z.number().int(),
});
export type HistoryResponse = z.infer<typeof HistoryResponse>;

/* ─── פאנלים (10 §1) ─── */

/** GET /api/panel/ai2?conversation= (10 §1) — לפי תשומת לב: תצוגה מקדימה אחרונה, מה שממתין, מה השיחה בנתה, היצירות שלי, הקשר נתונים. */
/** data-spread-publish-explain: מדידת הפרסום של הגרסה הנוכחית (null = לפני המדידה). */
export const PanelSpreadPerf = z.object({ ms: z.number(), level: z.string(), warningHe: z.string().nullable().optional() });
export type PanelSpreadPerf = z.infer<typeof PanelSpreadPerf>;
const PanelSpread = z.object({ key: z.string(), nameHe: z.string(), domainHe: z.string().nullable().optional(), status: z.string(), version: z.number().nullable().optional(), asOf: z.string().nullable(), perf: PanelSpreadPerf.nullable().optional(), /** data-spreads-private */ private: z.boolean().optional() });
export type PanelSpread = z.infer<typeof PanelSpread>;
const PanelLastRun = z.object({ id: z.string(), status: z.string(), finishedAt: z.string().nullable(), asOf: z.string().nullable().optional(), errorHe: z.string().nullable().optional() }).nullable();
export type PanelLastRun = z.infer<typeof PanelLastRun>;
export const PanelBuilt = z.object({
  kind: z.enum(['artifact', 'spread']), key: z.string(), nameHe: z.string(), type: z.string(), status: z.string(), version: z.number().nullable().optional(),
  lastAction: z.string(), at: z.string(), firstAt: z.string().optional(), lastRun: PanelLastRun.optional(),
});
export type PanelBuilt = z.infer<typeof PanelBuilt>;
export const PanelMineArtifact = z.object({
  slug: z.string(), nameHe: z.string(), type: z.string(), status: z.string(), version: z.number().nullable(), createdAt: z.string().nullable().optional(),
  lastRun: PanelLastRun, spreads: z.array(PanelSpread),
});
export type PanelMineArtifact = z.infer<typeof PanelMineArtifact>;
export const PanelWorld = z.object({ key: z.string(), nameHe: z.string(), status: z.enum(['ok', 'stale', 'failed', 'never']), asOf: z.string().nullable(), lastOkAt: z.string().nullable(), reasonHe: z.string().optional() });
export type PanelWorld = z.infer<typeof PanelWorld>;
/** conversation-admin-requests: בקשה מאייבי למנהל הבינה (panelAi1.requests). */
export const AdminRequestCard = z.object({
  id: z.string(),
  kind: z.string(),
  titleHe: z.string(),
  needHe: z.string(),
  findingsHe: z.string(),
  refs: z.object({ spreads: z.array(z.string()), artifacts: z.array(z.string()) }).partial().optional(),
  worldKey: z.string().nullable().optional(),
  requestedBy: z.array(z.object({ id: z.string(), nameHe: z.string() })),
  status: z.enum(['open', 'in_progress', 'done', 'declined']),
  adminConversationId: z.string().nullable().optional(),
  createdAt: z.string(),
  /** conversation-request-close-on-publish: פרסום השלים את העבודה ומנהל הבינה עוד לא סגר — "פורסם, ממתין לסגירה". */
  readyAt: z.string().nullable().optional(),
});
export type AdminRequestCard = z.infer<typeof AdminRequestCard>;
/** ...ומה שהמבקש רואה בפאנל אייבי (panelAi2.requests). */
export const MyAdminRequest = z.object({
  id: z.string(),
  kind: z.string(),
  titleHe: z.string(),
  status: z.enum(['open', 'in_progress', 'done', 'declined']),
  createdAt: z.string(),
  resultHe: z.string().nullable().optional(),
  decisionNoteHe: z.string().nullable().optional(),
  resolvedAt: z.string().nullable().optional(),
});
export type MyAdminRequest = z.infer<typeof MyAdminRequest>;
/** POST /api/admin-requests/:id/decide */
export const DecideRequestResponse = z.object({ status: z.enum(['in_progress', 'declined']), conversationId: z.string().nullable() });

export const PanelAi2Full = z.object({
  conversationId: z.string().nullable(),
  lastPreview: z
    .object({ block: z.unknown(), asOf: z.string().nullable().optional(), turnId: z.string().optional(), ref: z.object({ kind: z.enum(['artifact', 'spread']), key: z.string(), nameHe: z.string() }).nullable().optional() })
    .nullable()
    .optional(),
  waiting: z.object({
    consents: z.array(z.object({ consentId: z.string(), titleHe: z.string(), itemsTotal: z.number(), createdAt: z.string(), expiresAt: z.string() })),
    drafts: z.array(z.object({ slug: z.string(), nameHe: z.string(), type: z.string(), createdAt: z.string() })),
    failedRuns: z.array(z.object({ runId: z.string(), slug: z.string(), nameHe: z.string(), status: z.string(), errorHe: z.string(), finishedAt: z.string().nullable() })),
  }),
  built: z.array(PanelBuilt),
  mine: z.object({ artifacts: z.array(PanelMineArtifact), spreads: z.array(PanelSpread) }),
  data: z.object({ worlds: z.array(PanelWorld) }),
  /** 19 §1.6: התראות פתוחות של התזמונים של המשתמש. */
  alerts: z.array(z.object({ id: z.string(), kind: z.string(), messageHe: z.string(), createdAt: z.string(), trigger: z.object({ id: z.string(), nameHe: z.string(), slug: z.string() }) })).default([]),
  /** conversation-admin-requests: הבקשות שהמשתמש העביר למנהל (פתוחות, בטיפול, ושנסגרו ב-14 יום). */
  requests: z.array(MyAdminRequest).default([]),
});
export type PanelAi2Full = z.infer<typeof PanelAi2Full>;

export const PendingProposal = z.object({
  proposalId: z.string(),
  kind: ProposalKind,
  /** jsonb חופשי (05): "במספרים ובשמות". המסך מציג summaryHe/items אם יש, אחרת מפתח→ערך. */
  whatChanges: z.unknown(),
  /** api: { actor: 'ai1:<email>' | <name>, identityKind: 'agent'|'human', conversationId? }; המסך מציג "בינה-1", לא את המחרוזת. */
  proposedBy: z.union([z.string(), z.object({ actor: z.string(), identityKind: z.string().optional() }).passthrough()]),
  createdAt: IsoTs,
  worldNameHe: z.string().optional(),
  rationaleHe: z.string().nullable().optional(),
});
export type PendingProposal = z.infer<typeof PendingProposal>;

export const RecentProposal = z.object({
  proposalId: z.string(),
  kind: ProposalKind,
  state: z.enum(['approved', 'rejected', 'withdrawn', 'superseded']),
  summaryHe: z.string().nullable().optional(),
  noteHe: z.string().nullable().optional(),
  proposedBy: z.union([z.string(), z.object({ actor: z.string(), identityKind: z.string().optional() }).passthrough()]),
  createdAt: IsoTs,
  decidedAt: IsoTs.nullable().optional(),
  worldNameHe: z.string().optional(),
});
export type RecentProposal = z.infer<typeof RecentProposal>;

/** שורת הסנכרון לעולם (10 §1): המסך מציג, לא מחשב. */
export const WorldSync = z.discriminatedUnion('status', [
  z.object({ status: z.literal('ok'), lastOkAt: z.string() }),
  z.object({ status: z.literal('stale'), since: z.string().nullable(), lastOkAt: z.string().nullable() }),
  z.object({ status: z.literal('failed'), reasonHe: z.string(), since: z.string().nullable(), lastOkAt: z.string().nullable() }),
  z.object({ status: z.literal('running'), since: z.string(), lastOkAt: z.string().nullable() }),
  z.object({ status: z.literal('never') }),
]);
export type WorldSync = z.infer<typeof WorldSync>;

export const SyncTable = z.object({
  entity: z.string(),
  company: z.string(),
  signal: z.string().nullable(),
  rows: z.number(),
  asOf: z.string().nullable(),
  verifiedAt: z.string().nullable(),
  fallback: z.object({ from: z.string(), to: z.string(), reasonHe: z.string() }).nullable(),
  loading: z.object({ done: z.number(), total: z.number() }).nullable(),
});
export type SyncTable = z.infer<typeof SyncTable>;

export const PanelAlert = z.object({ id: z.string(), kind: z.string(), messageHe: z.string(), createdAt: z.string().nullable() }).passthrough();
export type PanelAlert = z.infer<typeof PanelAlert>;

/** צימוד שממתין לפרטי חיבור (02): בלי הקוד ובלי סודות — רק מזהה, עולמות, connRefs, תוקף. */
export const PendingPairing = z.object({
  pairingId: z.string(),
  createdAt: z.string(),
  expiresAt: z.string(),
  worlds: z.array(z.object({ key: z.string(), nameHe: z.string(), company: z.string().nullable(), driver: z.string() })),
  connRefs: z.array(z.string()),
});
export type PendingPairing = z.infer<typeof PendingPairing>;

export const PanelAi1Full = z.object({
  worlds: z.array(
    z.object({
      key: z.string(),
      nameHe: z.string(),
      type: z.string(),
      mode: z.string(),
      connector: z.enum(['online', 'disconnected', 'none']),
      lastSeen: z.string().nullable(),
      lastSync: z.string().nullable(),
      sync: WorldSync.optional(),
      /** 03 §2.2: לכל טבלה — האות, נכון ל-, אומת ב-(סבב מלא), נפילה פעילה, טעינה ראשונה בתהליך */
      tables: z.array(SyncTable).optional(),
      alerts: z.array(PanelAlert),
    }),
  ),
  syncRuns: z.array(
    z
      .object({
        id: z.string(),
        worldKey: z.string().optional(),
        status: z.string(),
        trigger: z.string().optional(),
        startedAt: z.string().nullable().optional(),
        finishedAt: z.string().nullable().optional(),
        error: z.string().nullable().optional(),
        summaryHe: z.string().optional(),
      })
      .passthrough(),
  ),
  users: z.array(z.object({ id: z.string(), nameHe: z.string(), role: Role, active: z.boolean() })),
  pendingProposals: z.array(PendingProposal),
  /** היסטוריה קצרה (30 יום): אושרה/נדחתה/בוטלה (בינה-1)/הוחלפה — לא ממתינות, רק לקריאה. */
  recentProposals: z.array(RecentProposal).default([]),
  pendingPairings: z.array(PendingPairing).default([]),
  consentsPending: z.number().int(),
  /** התראות ברמת המופע (api.restarted — 12 §1): לא של עולם, מוצגות מעל העולמות. */
  alerts: z.array(PanelAlert).default([]),
  /** conversation-admin-requests: בקשות משתמשים מאייבי (פתוחות ובטיפול). */
  requests: z.array(AdminRequestCard).default([]),
});
export type PanelAi1Full = z.infer<typeof PanelAi1Full>;

/** POST /api/tree/proposals/:id/decide {decision, noteHe} (05) */
export const DecideProposalResponse = z.object({ proposalId: z.string(), state: z.enum(['approved', 'rejected']) }).passthrough();

/** POST /api/connector/pairings/id/:pairingId/secrets {[connRef]: ConnSecret} → {connRefs} (02; api: hub.attachSecretsById) */
export const AttachSecretsResponse = z.object({ connRefs: z.array(z.string()) });

/* ─── קונסנט (08 §1) ─── */

/** 14: בקשת היקף (ממשק scoped כמו web) — hosts + תוקף + מטרה; האישור מעניק בדיוק את זה. */
export const ScopeRequestView = z.object({
  // 20: mail.send — recipients; mail.read — mailbox ('self') + since
  scope: z
    .object({ hosts: z.union([z.literal('*'), z.array(z.string())]).optional(), recipients: z.union([z.literal('*'), z.array(z.string())]).optional(), mailbox: z.literal('self').optional(), since: z.string().optional() })
    .passthrough(),
  until: IsoTs.nullable(),
  purposeHe: z.string(),
});
export type ScopeRequestView = z.infer<typeof ScopeRequestView>;

export const ConsentSummary = z.object({
  id: z.string(),
  titleHe: z.string(),
  interfaceId: z.string(),
  action: z.string(),
  status: ConsentStatus,
  itemsTotal: z.number().int(),
  expiresAt: IsoTs,
  createdAt: IsoTs,
  requestedBy: z.object({ id: z.string(), nameHe: z.string() }),
  scopeRequest: ScopeRequestView.nullable().optional(),
});
export type ConsentSummary = z.infer<typeof ConsentSummary>;
export const ConsentList = z.object({ consents: z.array(ConsentSummary) });

export const ConsentItemView = z.object({
  id: z.string(),
  seq: z.number().int(),
  summaryHe: z.string(),
  preview: z.unknown(),
  status: ConsentItemStatus,
});
export type ConsentItemView = z.infer<typeof ConsentItemView>;
/** GET /api/consents/:id?from=&limit= — הפריטים מדופדפים; התוכן נשלף בזהות הצופה. */
export const ConsentDetail = z.object({
  consent: ConsentSummary,
  items: z.array(ConsentItemView),
  from: z.number().int(),
  limit: z.number().int(),
});
export type ConsentDetail = z.infer<typeof ConsentDetail>;
/** POST /api/consents/:id/decide {approve: 'all'|ids, reject?: ids} → {status, approved, executed} (אומץ ב-apps/api, 08) */
export const ConsentDecideResponse = z.object({ status: ConsentStatus, approved: z.number().int(), executed: z.number().int() });

/* ─── ממשקי יצירה (09) ─── */

/** GET /api/artifacts/:slug/interfaces — אומץ ב-09 §1 (apps/api); 403 למי שאינו היוצר/admin. */
export const ArtifactInterface = z.object({
  interfaceId: z.string(),
  nameHe: z.string(),
  sideEffect: z.boolean(),
  mode: z.enum(['auto', 'consent']),
  /** 14: ממשק scoped — מה שהקוד מצהיר ומצב האישור: web = hosts; 20: mail.send = recipients. */
  scope: z
    .object({
      hosts: z.array(z.string()).default([]),
      approvedHosts: z.array(z.string()).default([]),
      recipients: z.array(z.string()).optional(),
      approvedRecipients: z.array(z.string()).optional(),
      status: z.enum(['approved', 'pending', 'missing', 'none']),
    })
    .passthrough()
    .optional(),
});
export type ArtifactInterface = z.infer<typeof ArtifactInterface>;
export const ArtifactInterfaces = z.object({ interfaces: z.array(ArtifactInterface) });

/** 26: POST /api/users/:id/login-link (admin) — הקישור (token ב-fragment) ותפוגה. */
export const LoginLinkResponse = z.object({ url: z.string(), expiresAt: z.string() });
